import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { earningsService, partnersService } from '../services/api';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';

const PendingPaymentsPage = () => {
  const [earnings, setEarnings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    partner_id: '',
    search: '',
    sortBy: 'invoice_uploaded_at',
    sortOrder: 'ASC'
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  useEffect(() => {
    loadPartners();
    loadPendingPayments();
  }, [pagination.page, pagination.limit, filters]);

  const loadPartners = async () => {
    try {
      const response = await partnersService.getAllPartners({ 
        status: 'approved',
        limit: 1000 
      });
      if (response.success) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadPendingPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await earningsService.getPendingPayments(params);

      if (response.success) {
        setEarnings(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Ödeme bekleyen hak edişler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading pending payments:', error);
      toast.error('Ödeme bekleyen hak edişler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleInvoicePreview = (earning) => {
    if (earning.invoice_file_path) {
      setSelectedInvoice(earning);
      setShowInvoiceModal(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Ödeme Bekleyen" />
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Ödeme Bekleyen (5 İş Günü Geçmiş)</h5>
        </div>
        <div className="card-body">
          {/* Filtreler */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Partner</label>
              <select
                className="form-select"
                value={filters.partner_id}
                onChange={(e) => handleFilterChange('partner_id', e.target.value)}
              >
                <option value="">Tümü</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Arama</label>
              <input
                type="text"
                className="form-control"
                placeholder="Müşteri, partner ara..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Sıralama</label>
              <select
                className="form-select"
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="invoice_uploaded_at_ASC">Fatura Tarihi (Eski → Yeni)</option>
                <option value="invoice_uploaded_at_DESC">Fatura Tarihi (Yeni → Eski)</option>
                <option value="net_amount_DESC">Tutar (Yüksek → Düşük)</option>
                <option value="net_amount_ASC">Tutar (Düşük → Yüksek)</option>
              </select>
            </div>
          </div>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : earnings.length === 0 ? (
            <div className="alert alert-info text-center">
              Ödeme bekleyen hak ediş bulunamadı.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Müşteri</th>
                      <th>Partner</th>
                      <th>Satış Tarihi</th>
                      <th>Fatura Yükleme Tarihi</th>
                      <th>İş Günü (Geçen)</th>
                      <th>Fatura</th>
                      <th className="text-end">Net Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map(earning => (
                      <tr key={earning.id}>
                        <td>
                          <div>
                            <strong>{earning.customer_name || '-'}</strong>
                            {earning.customer_phone && (
                              <div className="text-muted small">{earning.customer_phone}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <strong>{earning.partner_name}</strong>
                        </td>
                        <td>{formatDate(earning.sale_date)}</td>
                        <td>
                          {earning.invoice_uploaded_at ? (
                            formatDate(earning.invoice_uploaded_at)
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-warning">
                            {earning.business_days_elapsed} iş günü
                          </span>
                        </td>
                        <td>
                          {earning.invoice_file_path ? (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleInvoicePreview(earning)}
                            >
                              <Icon icon="solar:document-text-outline" className="me-1" />
                              Görüntüle
                            </button>
                          ) : (
                            <span className="badge bg-secondary">Fatura Yok</span>
                          )}
                        </td>
                        <td className="text-end">
                          <strong className="text-success">{formatCurrency(earning.net_amount)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', display: 'inline-block' }}
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ms-2 text-muted">
                    Toplam {pagination.total} kayıt
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return (
                          <li key={page} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fatura Önizleme Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Fatura Önizleme - {selectedInvoice.customer_name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedInvoice(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-3">
                  <a
                    href={selectedInvoice.invoice_file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary me-2"
                  >
                    <Icon icon="solar:download-outline" className="me-2" />
                    İndir
                  </a>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setSelectedInvoice(null);
                    }}
                  >
                    Kapat
                  </button>
                </div>
                <iframe
                  src={selectedInvoice.invoice_file_path}
                  style={{ width: '100%', height: '600px', border: '1px solid #ddd' }}
                  title="Fatura Önizleme"
                ></iframe>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}></div>
        </div>
      )}
    </MasterLayout>
  );
};

export default PendingPaymentsPage;

